import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, interval, Observable, of, Subscription } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import * as moment from 'moment';
import {
    BooleanIHandleError,
    TimeTrackingServiceProxy,
    StartTimerInputDto,
    StopTimerInputDto,
    AddTimeEntryInputDto,
    UpdateTimeEntryInputDto
} from '@shared/service-proxies/service-proxies';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { UserSignalrService } from '@app/shared/layout/change-status/user-signalr.service';
import { ActiveTimer, TimeEntry, AddTimeEntryInput, UpdateTimeEntryInput } from '../models/time-tracking.models';

const STORAGE_KEY = 'vf_active_timer';

@Injectable({ providedIn: 'root' })
export class TimeTrackingService implements OnDestroy {

    activeTimer$ = new BehaviorSubject<ActiveTimer | null>(this.loadStoredTimer());
    elapsedSeconds$ = new BehaviorSubject<number>(0);

    private tickSub: Subscription;
    private signalrTimerSub: Subscription;

    constructor(
        private _proxy: TimeTrackingServiceProxy,
        private _session: AppSessionService,
        private _userSignalr: UserSignalrService
    ) {
        this.syncElapsed();
        if (this.activeTimer$.value) {
            this.startTick();
        }
        // Cross-device timer sync. The backend broadcasts on every start/stop
        // to all of the current user's connected sessions. The originating
        // session also receives its own broadcast, which is harmless — we
        // overwrite local state with the server-truth payload.
        this.signalrTimerSub = this._userSignalr.timerUpdates.subscribe(payload => {
            this.handleRemoteTimerUpdate(payload);
        });
    }

    private handleRemoteTimerUpdate(payload: any): void {
        if (!payload) {
            return;
        }
        let action: string = payload.action || (payload.activeTimer ? 'start' : 'stop');
        if (action === 'stop' || !payload.activeTimer) {
            // Only clear if we actually have a running timer locally, otherwise
            // the stop event is a no-op (avoids redundant BehaviorSubject emits).
            if (this.activeTimer$.value) {
                this.saveTimer(null);
                this.activeTimer$.next(null);
                this.stopTick();
                this.elapsedSeconds$.next(0);
            }
            return;
        }
        let d: any = payload.activeTimer;
        let nextTimer: ActiveTimer = {
            id: d.id,
            taskId: d.taskId,
            taskName: d.taskName,
            startedAt: this.toUtcIso(d.startTime),
            userId: d.userId,
            projectId: d.projectId ?? undefined
        } as ActiveTimer;
        // Skip if we already have the same timer (idempotent — the broadcast
        // echoes back to the originating session after its own optimistic
        // update). Compare by id, falling back to taskId+startedAt for the
        // rare case the local state was set without an id.
        let current: ActiveTimer | null = this.activeTimer$.value;
        if (current && nextTimer.id && current.id === nextTimer.id) {
            return;
        }
        if (current && !nextTimer.id && current.taskId === nextTimer.taskId
            && current.startedAt === nextTimer.startedAt) {
            return;
        }
        this.saveTimer(nextTimer);
        this.activeTimer$.next(nextTimer);
        this.startTick();
    }

    private loadStoredTimer(): ActiveTimer | null {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    }

    private saveTimer(timer: ActiveTimer | null): void {
        if (timer) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(timer));
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }
    }

    private startTick(): void {
        this.stopTick();
        this.syncElapsed();
        this.tickSub = interval(1000).subscribe(() => this.syncElapsed());
    }

    private stopTick(): void {
        if (this.tickSub) { this.tickSub.unsubscribe(); }
    }

    private syncElapsed(): void {
        const timer = this.activeTimer$.value;
        if (!timer) { this.elapsedSeconds$.next(0); return; }
        const diffMs = Date.now() - new Date(timer.startedAt).getTime();
        this.elapsedSeconds$.next(Math.max(0, Math.floor(diffMs / 1000)));
    }

    private toUtcIso(dt: Date | string | undefined): string {
        if (!dt) return new Date().toISOString();
        const s = String(dt);
        return /Z$|[+-]\d{2}:\d{2}$/.test(s) ? s : s + 'Z';
    }

    get isRunning(): boolean {
        return !!this.activeTimer$.value;
    }

    get activeTimer(): ActiveTimer | null {
        return this.activeTimer$.value;
    }

    startTimer(taskId: number, taskName: string, projectId?: number | null): Observable<ActiveTimer> {
        return this._proxy.startTimer(new StartTimerInputDto({ taskId, projectId: projectId ?? undefined })).pipe(
            map(res => {
                const d = res.responseData;
                return {
                    id: d.id,
                    taskId: d.taskId,
                    taskName: d.taskName,
                    startedAt: this.toUtcIso(d.startTime),
                    userId: d.userId,
                    projectId: projectId ?? undefined
                } as ActiveTimer;
            }),
            tap(timer => {
                this.saveTimer(timer);
                this.activeTimer$.next(timer);
                this.startTick();
            }),
            catchError(() => {
                const timer: ActiveTimer = { taskId, taskName, startedAt: new Date().toISOString(), userId: 0 };
                this.saveTimer(timer);
                this.activeTimer$.next(timer);
                this.startTick();
                return of(timer);
            })
        );
    }

    stopTimer(): Observable<TimeEntry | null> {
        const current = this.activeTimer$.value;
        if (!current) return of(null);

        return this._proxy.stopTimer(new StopTimerInputDto({ entryId: current.id })).pipe(
            // Run through normalizeTimeEntry so durationSeconds is populated
            // and date/HH:mm are in viewer tz, matching every other read path.
            map(res => this.normalizeTimeEntry(res.responseData)),
            tap(() => {
                this.saveTimer(null);
                this.activeTimer$.next(null);
                this.stopTick();
                this.elapsedSeconds$.next(0);
            }),
            catchError(() => {
                this.saveTimer(null);
                this.activeTimer$.next(null);
                this.stopTick();
                this.elapsedSeconds$.next(0);
                return of(null);
            })
        );
    }

    /**
     * Stop the active timer if it's running on the given task. Used by callers
     * that archive / inactivate / delete a task so the elapsed time is preserved
     * as a time entry instead of being lost.
     */
    stopIfRunningOnTask(taskId: number): Observable<TimeEntry | null> {
        const current = this.activeTimer$.value;
        if (current && current.taskId === taskId) {
            return this.stopTimer();
        }
        return of(null);
    }

    /**
     * Stop the active timer if it's running on a task that belongs to the given
     * project. Triggered when a project is archived / inactivated / deleted.
     */
    stopIfRunningOnProject(projectId: number): Observable<TimeEntry | null> {
        const current = this.activeTimer$.value;
        if (current && (current as any).projectId === projectId) {
            return this.stopTimer();
        }
        return of(null);
    }

    loadActiveTimer(): void {
        this._proxy.getActiveTimer().pipe(
            map(res => res.responseData ?? null),
            catchError(() => of(undefined))
        ).subscribe(d => {
            if (d) {
                const timer: ActiveTimer = { id: d.id, taskId: d.taskId, taskName: d.taskName, startedAt: this.toUtcIso(d.startTime), userId: d.userId };
                this.saveTimer(timer);
                this.activeTimer$.next(timer);
                this.startTick();
            } else if (d === null) {
                // Backend explicitly returned no active timer — clear local state
                this.saveTimer(null);
                this.activeTimer$.next(null);
                this.stopTick();
            }
            // d === undefined means a backend error — keep existing localStorage state
        });
    }

    getTaskTimeEntries(taskId: number): Observable<TimeEntry[]> {
        return this._proxy.getTaskTimeEntries(taskId).pipe(
            map(res => (res.responseData || []).map(e => this.normalizeTimeEntry(e))),
            catchError(() => of([]))
        );
    }

    addTimeEntry(input: AddTimeEntryInput): Observable<TimeEntry> {
        const startDateTime = this.composeDateTime(input.date, input.startTime);
        const endDateTime = this.composeDateTime(input.date, input.endTime, input.startTime);
        const dto = new AddTimeEntryInputDto({ taskId: input.taskId, projectId: input.projectId ?? undefined, userId: input.userId ?? undefined, startTime: startDateTime, endTime: endDateTime, notes: undefined });
        return this._proxy.addTimeEntry(dto).pipe(
            map(res => this.normalizeTimeEntry(res.responseData))
        );
    }

    updateTimeEntry(input: UpdateTimeEntryInput): Observable<TimeEntry> {
        const startDateTime = this.composeDateTime(input.date, input.startTime);
        const endDateTime = this.composeDateTime(input.date, input.endTime, input.startTime);
        const dto = new UpdateTimeEntryInputDto({ id: input.id, userId: input.userId ?? undefined, startTime: startDateTime, endTime: endDateTime, notes: undefined });
        return this._proxy.updateTimeEntry(dto).pipe(
            map(res => this.normalizeTimeEntry(res.responseData))
        );
    }

    /**
     * Combine 'YYYY-MM-DD' + 'HH:mm' (interpreted in the viewer's PROFILE
     * timezone, falling back to browser local) into a UTC ISO string ('…Z').
     * Backend stores DateTime as UTC. When startTimeForCompare is provided and
     * the picked time is <= it, roll forward 1 day so end > start.
     */
    private composeDateTime(date: string, time: string, startTimeForCompare?: string): string {
        if (!date || !time) { return time || ''; }
        if (/T\d{2}:\d{2}/.test(time)) { return time; }
        const userOffset = this.getUserTzOffset();
        let m: moment.Moment;
        if (userOffset !== null) {
            // Build the picked wall-clock as if it were UTC, then mark its real
            // offset (keepLocalTime=true). .utc() converts to the actual UTC ms.
            m = moment.utc(`${date}T${time}:00`).utcOffset(userOffset, true);
        } else {
            // Fallback: browser local
            m = moment(`${date}T${time}:00`);
        }
        if (!m.isValid()) { return ''; }
        if (startTimeForCompare && time <= startTimeForCompare) {
            m.add(1, 'day');
        }
        return m.utc().toISOString();
    }

    /**
     * Read the viewer's profile timezone offset, normalized to a
     * moment-friendly "+HH:MM" / "-HH:MM" string. Returns null only when
     * we have nothing usable — caller falls back to the browser's local tz.
     *
     * Source priority:
     *   1. user.userTimeZoneOffset      — the user's profile tz
     *   2. user.companyTimeZoneOffset   — tenant default, used when the user
     *                                     hasn't set their own tz so the
     *                                     display still respects org-level tz
     *                                     instead of whatever the browser's
     *                                     OS happens to report.
     *
     * Backend stores both from TimeSpan.ToString(), which emits "HH:MM:SS"
     * (e.g. "-04:00:00"). Strip the trailing ":SS" to a clean ±HH:MM that
     * moment.utcOffset() parses cleanly across versions, and prepend "+" for
     * unsigned positive offsets like "05:30:00" (TimeSpan never emits "+").
     */
    private getUserTzOffset(): string | number | null {
        const u = (this._session && this._session.user) as any;
        if (!u) { return null; }
        const raw = (u.userTimeZoneOffset && String(u.userTimeZoneOffset).trim())
                 || (u.companyTimeZoneOffset && String(u.companyTimeZoneOffset).trim())
                 || '';
        if (!raw) { return null; }
        const match = raw.match(/^([+-]?)(\d{1,2}):(\d{2})(?::\d{2})?$/);
        if (match) {
            const sign = match[1] || '+';
            return `${sign}${match[2].padStart(2, '0')}:${match[3]}`;
        }
        return raw;
    }

    /** Parse a server UTC datetime and shift it to the viewer's profile timezone. */
    private toUserMoment(value: any): moment.Moment | null {
        if (!value) { return null; }
        const s = typeof value === 'string' ? value : String(value);
        const withTz = /Z$|[+-]\d{2}:\d{2}$/.test(s) ? s : s + 'Z';
        const m = moment.utc(withTz);
        if (!m.isValid()) { return null; }
        const userOffset = this.getUserTzOffset();
        if (userOffset !== null) {
            m.utcOffset(userOffset as any);
        } else {
            m.local();
        }
        return m;
    }

    private normalizeTimeEntry(raw: any): TimeEntry {
        if (!raw) { return raw; }
        const start = raw.startTime;
        const end = raw.endTime;
        return {
            ...raw,
            userName: raw.userName || raw.userFullName || '',
            // Derive date/time from the start timestamp in the viewer's profile timezone
            // so grouping and display follow the user's tz, not browser local.
            date: this.toDateString(start) || raw.date || '',
            startTime: this.toTimeString(start) || raw.startTime || '',
            endTime: this.toTimeString(end) || raw.endTime || '',
            // True elapsed seconds from the original UTC timestamps. Server's
            // DurationMinutes is whole-minute (DATEDIFF MINUTE) and rounds up
            // sub-minute timers — we don't trust it for display.
            durationSeconds: this.deriveDurationSeconds(start, end, raw.durationMinutes)
        } as TimeEntry;
    }

    /** Compute precise elapsed seconds from server UTC start/end strings.
     *  Falls back to (durationMinutes * 60) if the strings can't be parsed. */
    deriveDurationSeconds(start: any, end: any, durationMinutes: number): number {
        const sm = this.toUserMoment(start);
        const em = this.toUserMoment(end);
        if (sm && em) {
            const ms = em.valueOf() - sm.valueOf();
            if (!isNaN(ms) && ms >= 0) { return Math.floor(ms / 1000); }
        }
        return Math.max(0, (durationMinutes || 0) * 60);
    }

    private toDateString(value: any): string {
        const m = this.toUserMoment(value);
        return m ? m.format('YYYY-MM-DD') : '';
    }

    private toTimeString(value: any): string {
        const m = this.toUserMoment(value);
        return m ? m.format('HH:mm') : '';
    }

    deleteTimeEntry(id: number): Observable<BooleanIHandleError> {
        return this._proxy.deleteTimeEntry(id);
    }

    formatElapsed(totalSeconds: number): string {
        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = totalSeconds % 60;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }

    /**
     * Format a duration given in SECONDS — no rounding, every non-zero unit
     * is shown so totals and groups match the underlying time exactly.
     *   31     → "31s"
     *   90     → "1m 30s"
     *   3600   → "1h"
     *   3661   → "1h 1m 1s"
     *   3360   → "56m"   (51m 30s + 4m 30s = 56m exactly, no remainder)
     * Empty/zero shows "0s" so the column is never blank.
     */
    formatDuration(seconds: number): string {
        const total = Math.max(0, Math.floor(seconds || 0));
        if (total === 0) { return '0s'; }
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        const s = total % 60;
        const parts: string[] = [];
        if (h > 0) { parts.push(`${h}h`); }
        if (m > 0) { parts.push(`${m}m`); }
        if (s > 0) { parts.push(`${s}s`); }
        return parts.join(' ');
    }

    /**
     * Sum a list of entries by their actual elapsed seconds (not the server's
     * rounded-minute DurationMinutes), then format. This is what the user is
     * after when they see three 31-second entries — the total should reflect
     * 93 real seconds, not three rows that each rounded up to 1 minute.
     */
    formatTotalMinutes(entries: TimeEntry[]): string {
        const total = entries.reduce((acc, e) => acc + (e.durationSeconds || 0), 0);
        return this.formatDuration(total);
    }

    ngOnDestroy(): void {
        this.stopTick();
        if (this.signalrTimerSub) {
            this.signalrTimerSub.unsubscribe();
        }
    }
}
